import { useEffect, useState, useRef } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { motion, AnimatePresence, Variants } from 'motion/react';
import { getTodaysDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useSearch } from '@frontend/weave-router';
import { ContentLoader } from '@frontend/design-system';
import { CalendarLocationSelectorModal } from '../../components/calendar-location-selector-modal';
import { ColumnEntityReorderActions } from '../../components/calendar-view/calendar-column-entity-list-section/column-entity-reorder-actions';
import {
  ColumnEntityReorderStoreProvider,
  useColumnEntityReorderStore,
} from '../../components/calendar-view/providers';
import { MAX_CALENDAR_LOCATION_COUNT } from '../../constants';
import { useIsScheduleSmallModeWindow } from '../../hooks';
import { useGetCalendarV2QueriesData } from '../../hooks/use-get-calendar-v2-queries-data';
import { CalendarProvider, useCalendarStore, useSyncCalendarLocation } from '../../stores';
import { CalendarHeader } from '../CalendarV3/components/header';
import { SchedulePulseTrayContent } from '../schedule-pulse-panel';
import { CalendarTimeLineView } from './CalendarTimeLineView';

dayjs.extend(utc);

const CalendarV2Component = () => {
  const { t } = useTranslation('schedule');

  const { selectedLocationIds } = useAppScopeStore();

  const [selectedMultiLocationIds, setSelectedMultiLocationIds] = useState<string[]>(selectedLocationIds ?? []);
  const performedSearch = useRef(false);

  const setDefaultSelections = (locationIds: string[]) => {
    setSelectedMultiLocationIds(locationIds);
  };

  const searchParams = useSearch({ from: '/schedule/calendar' });

  const { calendarDate } = useCalendarStore(['calendarDate']);
  const {
    filteredAppointments,
    filteredProviderList,
    calendarCounts,
    providersOfficeHours,
    providerExceptions,
    officeHours,
    officeExceptions,
    isLoading,
  } = useGetCalendarV2QueriesData();
  const { isInReorderMode } = useColumnEntityReorderStore(['isInReorderMode']);

  useEffect(() => {
    const searchAppointment = () => {
      const element = document.querySelector(`[data-event-id="${searchParams.appointmentId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        (element as HTMLElement).click();
        performedSearch.current = true;
      }
    };

    if (
      !searchParams.appointmentId ||
      searchParams.currentDate !== calendarDate ||
      isLoading ||
      performedSearch.current
    ) {
      return;
    }
    if (selectedMultiLocationIds.length > MAX_CALENDAR_LOCATION_COUNT) return;

    const timeoutId = setTimeout(searchAppointment, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchParams?.appointmentId, searchParams.currentDate, isLoading, calendarDate, selectedMultiLocationIds.length]);

  return (
    <>
      <section css={pageStyle} id='calendar-view-wrapper'>
        {isInReorderMode ? (
          <ColumnEntityReorderActions />
        ) : (
          <CalendarHeader
            calendarEventsCount={calendarCounts.total}
            unconfirmedCalendarEventsCount={calendarCounts.unconfirmed}
            insuranceAlertCalendarEventsCount={calendarCounts.insurance}
            calendarTodayDate={getTodaysDate('MM/DD/YYYY')}
          />
        )}
        <div css={{ position: 'relative', overflow: 'auto' }}>
          <ContentLoader
            show={isLoading}
            message={t('Loading appointments...')}
            css={{ justifyContent: 'flex-start', paddingTop: 'calc(50vh - 150px)' }}
          />

          <CalendarTimeLineView
            isLoading={isLoading}
            filteredAppointments={filteredAppointments}
            filteredProviderList={filteredProviderList}
            providerExceptions={providerExceptions}
            providersOfficeHours={providersOfficeHours}
            officeHours={officeHours}
            officeExceptions={officeExceptions}
          />
        </div>
      </section>
      <CalendarLocationSelectorModal onSelect={setDefaultSelections} />
    </>
  );
};

const fadeVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const CalendarV2Wrapper = () => {
  const { isSmallModeWindow } = useIsScheduleSmallModeWindow();

  useSyncCalendarLocation();

  return (
    <>
      <AnimatePresence mode='wait'>
        {isSmallModeWindow ? (
          <motion.div
            key='list-view'
            variants={fadeVariants}
            initial='initial'
            animate='animate'
            exit='exit'
            css={{ height: '100%' }}
          >
            <SchedulePulseTrayContent isSmallModeWindow />
          </motion.div>
        ) : (
          <motion.div
            key='calendar-view'
            variants={fadeVariants}
            initial='initial'
            animate='animate'
            exit='exit'
            css={{ height: '100%' }}
          >
            <ColumnEntityReorderStoreProvider>
              <CalendarV2Component />
            </ColumnEntityReorderStoreProvider>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const CalendarV2 = () => (
  <CalendarProvider>
    <CalendarV2Wrapper />
  </CalendarProvider>
);

const pageStyle = css({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  position: 'relative',
  containerType: 'inline-size',
});
